<template>
  <el-select
    v-model="establishmentModel"
    @change="establishmentChanged"
    autocomplete="new-password"
    :placeholder="$t('COMMON.ESTABLISHMENT')"
    :filterable="filterable"
    :multiple="multiple"
    :disabled="disabled"
    remote
    :remote-method="getEstablishments"
    :loading="loading"
  >
    <el-option
      v-if="showAll"
      :value="null"
      :label="allOptionLabel || $t('COMMON.ALL_ESTABLISHMENT')"
    >
    </el-option>
    <el-option v-if="allowNone" :value="null" :label="$t('COMMON.NONE')"> </el-option>
    <el-option
      v-for="establishment in establishments"
      :key="establishment.id"
      :value="establishment.id"
      :label="optionLabel(establishment)"
    >
      <span>{{ establishment.company_name }} - {{ establishment.email }}</span>
      <el-tag
        v-if="isInactive(establishment)"
        size="mini"
        type="danger"
        class="ml-2"
      >
        {{ $t("ESTABLISHMENTS.INACTIVE") }}
      </el-tag>
    </el-option>
  </el-select>
</template>

<script>
import { Option, Select, Tag } from "element-ui";

export default {
  name: "establishment-selector",

  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Tag.name]: Tag,
  },

  props: {
    disabled: {
      type: Boolean,
      default: false,
      description: "Disable the input",
    },
    showAll: {
      type: Boolean,
      default: true,
      description: "Show the option all",
    },
    allOptionLabel: {
      type: String,
      default: null,
      description:
        "Custom label for the all option, e.g. to name the filter when a list has two establishment filters",
    },
    allowNone: {
      type: Boolean,
      default: false,
      description: "Show the option none",
    },
    filterable: {
      type: Boolean,
      default: true,
      description: "Can filter",
    },
    multiple: {
      type: Boolean,
      default: false,
      description: "Can filter",
    },
    establishment: {
      type: String,
      default: null,
      description: "Establishment id",
    },
    filterOrganization: {
      type: String,
      default: null,
      description: "Organization id",
    },
    filterServiceCenter: {
      type: String,
      default: null,
      description: "Service center id",
    },
    bypassScope: {
      type: Boolean,
      default: false,
      description: "Bypass establishment scope restriction",
    },
    onlyActive: {
      type: Boolean,
      default: false,
      description:
        "Only list active establishments (creation flows). The currently selected establishment is always loadable so existing records still display.",
    },
    onlyWeeklyReportsEnabled: {
      type: Boolean,
      default: false,
      description:
        "Only list establishments that produce weekly reports. Like onlyActive, the selected establishment is always loadable.",
    },
  },

  data() {
    return {
      establishmentModel: this.establishment,
      establishments: {},
    };
  },

  setup() {},

  created() {
    this.getEstablishments(null, this.establishment);
  },

  methods: {
    async getEstablishments(query = null, id = null) {
      try {
        this.loading = true;
        let params = {
          sort: "company_name",
          filter: {
            ...(query ? { search: encodeURIComponent(query) } : {}),
            ...(id ? { id: id } : {}),
            // En création (onlyActive), n'affiche que les établissements actifs.
            // On n'applique PAS ce filtre quand on charge un id précis afin que la
            // valeur déjà sélectionnée d'un enregistrement existant reste visible.
            ...(this.onlyActive && !id ? { active: 1 } : {}),
            ...(this.onlyWeeklyReportsEnabled && !id ? { weekly_reports_enabled: 1 } : {}),
          },
          page: {
            number: 1,
            size: 10,
          },
        };

        if (this.filterOrganization) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              organization: this.filterOrganization,
            },
          };
        }

        if (this.filterServiceCenter) {
          params = {
            ...params,
            filter: {
              ...params.filter,
              serviceCenter: this.filterServiceCenter,
            },
          };
        }

        if (this.bypassScope) {
          params = {
            ...params,
            bypassScope: true,
          };
        }

        await this.$store.dispatch("establishments/list", params);
        const establishmentsArr = await this.$store.getters["establishments/list"];
        this.establishments = {};
        establishmentsArr.forEach((establishment) => {
          this.establishments[establishment.id] = establishment;
        });
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.loading = false;
      }
    },

    establishmentChanged(establishment) {
      const establishmentSelected = Object.values(this.establishments).find(
        (item) => item.id === establishment
      );
      this.$emit("establishmentChanged", establishment, establishmentSelected);
    },

    isInactive(establishment) {
      return (
        !!establishment &&
        (establishment.active === false || establishment.active === 0)
      );
    },

    optionLabel(establishment) {
      const base = `${establishment.company_name} - ${establishment.email}`;
      return this.isInactive(establishment)
        ? `${base} (${this.$t("ESTABLISHMENTS.INACTIVE")})`
        : base;
    },
  },

  watch: {
    establishment(establishment) {
      if (establishment) {
        this.establishmentModel = establishment;
      } else {
        this.establishmentModel = null;
      }
      this.getEstablishments(null, this.establishment);
    },
    filterOrganization(filterOrganization) {
      this.getEstablishments();
    },
    filterServiceCenter: {
      handler: function (establishment) {
        this.getEstablishments();
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
